import React from 'react';
import { Search, Bell, Globe, ChevronDown, Sparkles, Command, PanelLeftClose, PanelLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { PERSONAS } from '@/data/seed';
import BrandMark from '@/components/Brand';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

export const TopBar = () => {
  const { persona, setPersonaId, language, setLanguage, notifications, sidebarCollapsed, setSidebarCollapsed } = useApp();

  return (
    <header className="sticky top-0 z-30 h-16 bg-onyx text-background border-b border-onyx pattern-arabesque">
      <div className="h-full px-5 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden md:inline-flex text-white/80 hover:text-white hover:bg-white/10 h-9 w-9"
        >
          {sidebarCollapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </Button>
        <Link to="/app/command-center" className="flex items-center gap-2">
          <BrandMark tone="ivory" />
        </Link>

        <div className="hidden md:flex items-center mx-2 px-2 py-1 rounded-md text-[10px] uppercase tracking-widest text-primary border border-primary/30 bg-primary/10">
          <Sparkles className="h-3 w-3 mr-1" /> Prototype
        </div>

        <div className="flex-1 max-w-xl mx-auto">
          <label htmlFor="global-search" className="sr-only">Search debtors, cases, portfolios, audit events</label>
          <div className="group flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 h-9 transition-colors">
            <Search className="h-4 w-4 text-white/60" aria-hidden="true" />
            <input
              id="global-search"
              type="text"
              aria-label="Search debtors, cases, portfolios, audit events"
              placeholder="Search debtors, cases, portfolios, audit events…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/40 text-white"
            />
            <kbd className="hidden md:inline-flex items-center gap-1 text-[10px] text-white/40 border border-white/15 rounded px-1.5 py-0.5" aria-hidden="true">
              <Command className="h-2.5 w-2.5" /> K
            </kbd>
          </div>
        </div>

        {/* Language toggle */}
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Switch language to ${language === 'EN' ? 'Arabic' : 'English'}`}
          onClick={() => setLanguage(language === 'EN' ? 'AR' : 'EN')}
          className="text-white/80 hover:text-white hover:bg-white/10"
        >
          <Globe className="h-4 w-4 mr-1.5" aria-hidden="true" />
          {language === 'EN' ? 'EN' : 'AR'}
        </Button>

        {/* Notifications */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="text-white/80 hover:text-white hover:bg-white/10 relative">
              <Bell className="h-4 w-4" />
              <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-primary ring-2 ring-onyx" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <p className="text-sm font-semibold">Notifications</p>
              <Badge variant="outline" className="text-[10px]">{notifications.length}</Badge>
            </div>
            <div className="max-h-80 overflow-y-auto scrollbar-thin">
              {notifications.map((n) => (
                <div key={n.id} className="px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted cursor-pointer">
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-sm font-medium">{n.title}</p>
                    <span className="text-[10px] text-muted-foreground">{n.time}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{n.body}</p>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Persona switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10">
              <Avatar className="h-7 w-7">
                <AvatarFallback className="bg-primary text-primary-foreground text-[11px] font-semibold">{persona.initials}</AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start leading-tight">
                <span className="text-[11px] text-white/60">{persona.short}</span>
                <span className="text-xs font-medium">{persona.user}</span>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-white/60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Switch persona (demo)</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {PERSONAS.map((p) => (
              <DropdownMenuItem key={p.id} onClick={() => setPersonaId(p.id)} className="flex items-center gap-2 py-2">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className={`text-[11px] font-semibold ${p.id === persona.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>{p.initials}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{p.short}</span>
                  <span className="text-[11px] text-muted-foreground">{p.user}</span>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default TopBar;
